import { visibleCatalog, type CatalogDestination, type CatalogEntry, type CatalogGate } from './catalog';
import { MARKETPLACE, PROJECT_180 } from './preview';

/**
 * The page a visitor is shown. Editorial is the Labs page; the others are E10 arms.
 * `editorial_bench` is the editorial page's placement in Lab Bench's style.
 */
export type LayoutChoice = 'editorial' | 'school_day' | 'lab_bench' | 'editorial_bench';
export type LandingVariant = 'control' | 'school_day' | 'lab_bench' | 'editorial_bench';

/**
 * The landing experiment decides only for its two arms. Its control, and anyone it has not
 * assigned, gets the editorial page.
 */
export function chooseLayout(landing: LandingVariant | null): LayoutChoice {
  return landing === 'school_day' || landing === 'lab_bench' || landing === 'editorial_bench' ? landing : 'editorial';
}

const LANDING_VARIANTS: readonly string[] = ['control', 'school_day', 'lab_bench', 'editorial_bench'];

/**
 * `?layout=<variant>` for builds without PostHog: draft PR previews and local dev, which
 * never read the flag. Production always has a key, so `prod` turns this off there, and an
 * override is never an assignment, so it records no exposure.
 */
export function layoutOverride(search: string, prod: boolean): LandingVariant | null {
  if (prod) return null;
  const value = new URLSearchParams(search).get('layout');
  return value !== null && LANDING_VARIANTS.includes(value) ? value as LandingVariant : null;
}

export type ListingStatus = 'open' | 'in_development' | 'coming_soon' | 'planned' | 'idea';
export type SchoolPeriod = 'before_bell' | 'in_class' | 'planning' | 'after_bell';

export interface Listing {
  entry: CatalogEntry;
  status: ListingStatus;
  /** Present only when the listing can be opened. */
  href?: string;
  period: SchoolPeriod;
}

/** Where each product sits in a teacher's day. New products fall back to their first need. */
const PERIODS: Record<string, SchoolPeriod> = {
  one_step_ahead: 'before_bell',
  objectives: 'before_bell',
  sites: 'in_class',
  rubric: 'in_class',
  collab: 'planning',
  planner: 'planning',
  profile: 'after_bell',
  writing_feedback: 'planning',
  ai_detector: 'in_class',
  [MARKETPLACE.id]: 'planning',
  jobs: 'after_bell',
};
const NEED_PERIOD = { plan: 'before_bell', teach: 'in_class', grow: 'after_bell' } as const;

export const SCHOOL_PERIODS: { id: SchoolPeriod; time: string; label: string; title: string; why: string }[] = [
  { id: 'before_bell', time: '6:45', label: 'Before the bell', title: 'Something new on the plan.',
    why: 'A unit you have never taught, a worksheet someone handed you. Get on top of it before first period.' },
  { id: 'in_class', time: '10:15', label: 'In class', title: 'Something for the room.',
    why: 'A page to put up on the board, and a fair read on the writing that comes back.' },
  { id: 'planning', time: '1:30', label: 'Planning period', title: 'Work with the people down the hall.',
    why: 'Share what you made with the colleagues you trust, and check how your own messages come across.' },
  { id: 'after_bell', time: '4:00', label: 'After the bell', title: 'The rest of your career.',
    why: 'Where you teach next, what you have built, and what is happening in your district. Some of these are still ideas.' },
];

/**
 * Every listed product except Project 180, which both new layouts feature on its own.
 * Visibility is resolved by the same allowlist as the editorial page, so a hidden tool
 * cannot appear here. `hrefs` is the caller's destination map; a destination without one
 * is listed as in development rather than linked.
 */
export function landingListings(
  visibility: Partial<Record<CatalogGate, boolean>>,
  hrefs: Partial<Record<CatalogDestination, string | undefined>>,
): Listing[] {
  return visibleCatalog(visibility)
    .filter(entry => entry.id !== PROJECT_180.id)
    .map(entry => {
      const href = entry.destination ? hrefs[entry.destination] : undefined;
      const status: ListingStatus = entry.size === 'idea' ? 'idea'
        : entry.id === MARKETPLACE.id ? 'planned'
          : href ? 'open' : 'in_development';
      return { entry, status, href, period: PERIODS[entry.id] ?? NEED_PERIOD[entry.needs[0] ?? 'grow'] };
    });
}
